import { Button, Checkbox, Divider, FormControlLabel, Menu, MenuItem, TextField } from "@mui/material";
import BadgeFulfillment, { BADGE_FULFILLMENT_STATUS_MAP } from "elements/BadgeFulfillment";
import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ApiRequest } from "GlobalFunctions";
import { PiCaretLeft } from "react-icons/pi";
import CancelOrderDialog from "./components/CancelOrderDialog";
import ConfirmStatusChangeDialog from "./components/ConfirmStatusChangeDialog";
import ConfirmOrderChangeDialog from "./components/ConfirmOrderChangeDialog";
import EventLogs from "elements/EventLogs";
import { calculateOrderTaxes, CalculatedOrderResult } from "./CalculateOrderTaxes";
import { useSnackbar } from "notistack";


export default function OrderEditor(props: any) {

    const { enqueueSnackbar } = useSnackbar();
    const [sendNotification, setSendNotification] = useState(false);
    const [productQuantities, setProductQuantities] = useState<{[key: number]: number}>({});
    const [deletedProducts, setDeletedProducts] = useState<{[key: number]: boolean}>({});
    const [hasChanges, setHasChanges] = useState(false);
    const [reason, setReason] = useState('');
  
  const formatPrice = (price: number | string) => {
    return Number(price).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatTaxRate = (taxRate: string) => {
    // Remove the % sign if it exists
    const numericRate = parseFloat(taxRate.replace('%', ''));
    
    // Check if it's an integer
    if (Number.isInteger(numericRate)) {
      return `${numericRate}%`;
    }
    
    // If it has decimals, show up to 3 decimal places (removing trailing zeros)
    return `${numericRate.toFixed(3).replace(/\.?0+$/, '')}%`;
  };
  
    const { setLoading } = props;
    const { order_id } = useParams();
    const navigate = useNavigate();
    const [orderDetails, setOrderDetails] = useState<any>(null);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [statusMenuWidth, setStatusMenuWidth] = useState<number | undefined>(undefined);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState<string>('');
    const [statusChangeLoading, setStatusChangeLoading] = useState(false);

    
    useEffect(() => {
      if (order_id) {
        loadOrderDetails();
      }
    }, [order_id]);

    useEffect(() => {
      if (!orderDetails?.data) {
        return;
      }
    }, [orderDetails, statusMenuWidth]);

    // Calculate order totals when quantities change
    const calculatedOrder = useMemo<CalculatedOrderResult | null>(() => {
      if (!orderDetails?.data) return null;
      
      const { order, products } = orderDetails.data;
      return calculateOrderTaxes(
        order,
        products,
        productQuantities,
        deletedProducts,
        order.tax_included || false
      );
    }, [orderDetails, productQuantities, deletedProducts]);

    const loadOrderDetails = async () => {
      setLoading(true);
      await ApiRequest({
        method: 'GET',
        url: '/companies/orders/details',
        query: { order_id },
        setResponse: (response: any) => {
          setOrderDetails(response);
          console.log("Order Details:", response);
          
          // Initialize product quantities
          const initialQuantities: {[key: number]: number} = {};
          response.data.products.forEach((product: any, index: number) => {
            initialQuantities[index] = product.quantity;
          });
          setProductQuantities(initialQuantities);
        }
      });
      setLoading(false);
    };

  const handleQuantityChange = (index: number, newQuantity: string) => {
    // If product is deleted, don't allow changes
    if (deletedProducts[index]) return;

    // If empty string, set to 0 and mark as deleted
    if (newQuantity === '') {
      setProductQuantities(prev => ({
        ...prev,
        [index]: 0
      }));
      setDeletedProducts(prev => ({
        ...prev,
        [index]: true
      }));
      setHasChanges(true);
      return;
    }

    const quantity = parseInt(newQuantity);
    
    // Only update if it's a valid number
    if (!isNaN(quantity) && quantity >= 0) {
      // If quantity is 0, mark as deleted
      if (quantity === 0) {
        setDeletedProducts(prev => ({
          ...prev,
          [index]: true
        }));
      }
      
      setProductQuantities(prev => ({
        ...prev,
        [index]: quantity
      }));
      
      // Check if there are changes
      const originalQuantity = orderDetails.data.products[index].quantity;
      if (quantity !== originalQuantity) {
        setHasChanges(true);
      } else {
        // Check if any other product has changes or any product is deleted
        const hasOtherChanges = orderDetails.data.products.some((product: any, idx: number) => {
          if (idx === index) return deletedProducts[idx] || false;
          return productQuantities[idx] !== product.quantity || deletedProducts[idx];
        });
        setHasChanges(hasOtherChanges);
      }
    }
  };

  const handleRemoveProduct = (index: number) => {
    // Only mark as deleted, don't change quantity
    setDeletedProducts(prev => ({
      ...prev,
      [index]: true
    }));
    setHasChanges(true);
  };

  const [updateLoading, setUpdateLoading] = useState(false);
  const [confirmChangeDialogOpen, setConfirmChangeDialogOpen] = useState(false);
  const [chargeFailed, setChargeFailed] = useState(false);
  const [paymentBlocked, setPaymentBlocked] = useState(false);

  const handleUpdateOrderClick = () => {
    if (!calculatedOrder || !hasChanges || !reason.trim()) return;

    // Check if payment is already blocked (from local state or server)
    const paymentAttemptInfo = orderDetails?.data?.paymentAttemptInfo;
    const isBlocked = paymentBlocked || (paymentAttemptInfo && paymentAttemptInfo.blocked);
    
    // Check if there are previous failed attempts (from server or local state)
    const hasPreviousFailedAttempts = chargeFailed || (paymentAttemptInfo && paymentAttemptInfo.attemptCount > 0);
    
    if (isBlocked) {
      setPaymentBlocked(true);
      setChargeFailed(true); // Show the failed options screen
      setConfirmChangeDialogOpen(true);
      return;
    }

    // If there are previous failed attempts and it's a collect type, show failed options
    if (hasPreviousFailedAttempts && calculatedOrder.differenceType === 'collect') {
      setChargeFailed(true);
      setConfirmChangeDialogOpen(true);
      return;
    }

    // If there's a charge or refund, show confirmation dialog
    if (calculatedOrder.differenceType !== 'none' && Math.abs(calculatedOrder.difference) > 0) {
      setConfirmChangeDialogOpen(true);
    } else {
      // No payment change, proceed directly
      handleUpdateOrder();
    }
  };

  const handleUpdateOrder = async () => {
    if (!calculatedOrder || !hasChanges || !reason.trim()) return;

    setUpdateLoading(true);

    // Prepare products data with order_product_id, new quantity, and deleted flag
    const productsToUpdate = orderDetails.data.products.map((product: any, index: number) => ({
      order_product_id: product.order_product_id,
      quantity: deletedProducts[index] ? product.quantity : (productQuantities[index] ?? product.quantity),
      deleted: deletedProducts[index] || false
    }));

    await ApiRequest({
      method: 'POST',
      url: '/companies/orders/updateOrder',
      body: {
        order_id,
        products: productsToUpdate,
        subtotal_price: calculatedOrder.newSubtotal,
        total_tax: calculatedOrder.newTotalTax,
        total_price: calculatedOrder.newTotal,
        discriminated_taxes: calculatedOrder.discriminatedTaxes,
        reason: reason.trim(),
        send_notification: sendNotification,
        difference_type: calculatedOrder.differenceType,
        difference_amount: Math.abs(calculatedOrder.difference)
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          setConfirmChangeDialogOpen(false);
          enqueueSnackbar('Order updated successfully', { variant: 'success' });
          navigate(`/orders/${order_id}`);
        } else {
          // If it's a collect type (charging customer), show failed options
          if (calculatedOrder.differenceType === 'collect') {
            setChargeFailed(true);
            // Update blocked state from response
            if (response.blocked) {
              setPaymentBlocked(true);
            }
          } else {
            enqueueSnackbar(response.message || 'Error updating order', { variant: 'error' });
          }
        }
      },
      onError: (error: any) => {
        // If it's a collect type (charging customer), show failed options
        if (calculatedOrder.differenceType === 'collect') {
          setChargeFailed(true);
        } else {
          enqueueSnackbar(error.message || 'Error updating order', { variant: 'error' });
        }
      }
    });

    setUpdateLoading(false);
  };

  const handleEditPaymentInfo = async (cardData: { nameOnCard: string; cardNumber: string; expiryDate: string; cvv: string }) => {
    if (!calculatedOrder || !hasChanges || !reason.trim()) return;
    
    setUpdateLoading(true);

    // Prepare products data with order_product_id, new quantity, and deleted flag
    const productsToUpdate = orderDetails.data.products.map((product: any, index: number) => ({
      order_product_id: product.order_product_id,
      quantity: deletedProducts[index] ? product.quantity : (productQuantities[index] ?? product.quantity),
      deleted: deletedProducts[index] || false
    }));
    
    await ApiRequest({
      method: 'POST',
      url: '/companies/orders/updatePaymentAndCharge',
      body: {
        order_id,
        card_data: {
          name_on_card: cardData.nameOnCard,
          card_number: cardData.cardNumber,
          expiry_date: cardData.expiryDate,
          cvv: cardData.cvv
        },
        difference_amount: Math.abs(calculatedOrder.difference),
        // Include all order update data
        products: productsToUpdate,
        subtotal_price: calculatedOrder.newSubtotal,
        total_tax: calculatedOrder.newTotalTax,
        total_price: calculatedOrder.newTotal,
        discriminated_taxes: calculatedOrder.discriminatedTaxes,
        reason: reason.trim(),
        send_notification: sendNotification
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          setConfirmChangeDialogOpen(false);
          enqueueSnackbar('Payment updated and charged successfully', { variant: 'success' });
          navigate(`/orders/${order_id}`);
        } else {
          // Update blocked state from response
          if (response.blocked) {
            setPaymentBlocked(true);
          }
          enqueueSnackbar(response.message || 'Error processing payment', { variant: 'error' });
        }
      },
      onError: (error: any) => {
        enqueueSnackbar(error.message || 'Error processing payment', { variant: 'error' });
      }
    });
    
    setUpdateLoading(false);
  };

  const handleSendFailedNotification = async () => {
    if (!calculatedOrder) return;
    
    setUpdateLoading(true);
    
    await ApiRequest({
      method: 'POST',
      url: '/companies/orders/sendPaymentFailedNotification',
      body: {
        order_id,
        amount_to_charge: Math.abs(calculatedOrder.difference)
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          setConfirmChangeDialogOpen(false);
          enqueueSnackbar('Payment failed notification sent to customer', { variant: 'success' });
          navigate(`/orders/${order_id}`);
        } else {
          enqueueSnackbar(response.message || 'Error sending notification', { variant: 'error' });
        }
      },
      onError: (error: any) => {
        enqueueSnackbar(error.message || 'Error sending notification', { variant: 'error' });
      }
    });
    
    setUpdateLoading(false);
  };

  if (!orderDetails) {
    return null;
  }

  const { order, shipping, products, store, custom_invoice, same_as_shipping, shipping_info } = orderDetails.data;

  // Format the status text
  const formatShippingStatus = (status: string) => {
    if (!status) return '';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Format date for shipping info
  const formatShippingDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Function to copy tracking number to clipboard
  const copyTrackingNumber = (trackingNumber: string) => {
    navigator.clipboard.writeText(trackingNumber);
  };

  return (
    <div id="orders-details" className="order-details">
      <div className="order-details-header">
        <div className="order-details-header-left">
          <span className="order-details-number" onClick={() => navigate(`/orders/${order_id}`)}>
            <PiCaretLeft size={16} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
            #{order.order_id}
          </span>

            <span className="order-details-no-link">
            <PiCaretLeft size={16} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
            Edit Order
          </span>
        </div>
      </div>

      <div className="order-details-meta">
        <div className="edit-order-tools">
            <BadgeFulfillment code={order.fulfillment_status_code} additional_text={`(${products.length})`} showDropdownIcon={false} />
            <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{
                    padding: "4px 10px",
                    borderRadius: "5px !important",
                    fontSize: "14px",
                    fontWeight: "500",
                    backgroundColor: "#ECEBFF",
                    color: "#7963FF",
                    lineHeight: "150%",
                    "&:hover": {
                        backgroundColor: "#ECEBFF",
                        color: "#7963FF",
                    }
                }}
            >
                + Add Product
            </Button>
        </div>
      </div>

      <div className="order-details-grid">
        <div className="left-editor-wrapper">
            <section id="order-details-products-area" className="order-details-card">
            <div className="order-details-section">

                <div className="order-details-section-header">
                <span className="order-details-section-title">Delivery Method</span>
                <span className="order-details-section-subtitle">{order.shipping_method}</span>

                {shipping_info && shipping_info.tracking_number && (
                    <div className="shipping-tracking-container">
                    {orderDetails?.data?.is_shippo && (
                        <div className="shipping-tracking-header">
                        <span className="shipping-tracking-title">{formatShippingStatus(shipping_info.status || shipping_info.tracking_status)}</span>
                        <span className="shipping-tracking-date">{formatShippingDate(shipping_info.object_updated)}</span>
                        </div>
                    )}

                    <div className="shipping-tracking-body">
                        <div className="shipping-tracking-number">
                        {(orderDetails?.data?.is_shippo ? shipping_info.provider_image_75 : shipping_info.logo_url) && (
                            <img 
                            src={orderDetails?.data?.is_shippo ? shipping_info.provider_image_75 : shipping_info.logo_url} 
                            alt="Carrier Logo" 
                            className="shipping-carrier-logo"
                            />
                        )}
                        <span className="tracking-label">Tracking Number:</span>
                        <span className="tracking-number">{shipping_info.tracking_number}</span>
                        <a 
                            onClick={() => copyTrackingNumber(shipping_info.tracking_number)}
                            className="copy-tracking-button"
                        >
                            Copy
                        </a>
                        </div>

                        {shipping_info.tracking_url_provider && (
                        <div className="shipping-tracking-actions">
                            <a 
                            href={shipping_info.tracking_url_provider} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="tracking-link"
                            >
                            View Details In {orderDetails?.data?.is_shippo ? (shipping_info.rate_details?.provider?.toUpperCase() || 'Carrier') : (shipping_info.provider || 'Carrier')}
                            </a>
                        </div>
                        )}
                    </div>
                    </div>
                )}

                </div>

                <div className="order-details-products">
                {products.map((product: any, index: number) => {
                    const isDeleted = deletedProducts[index] || false;
                    return (
                    <article key={index} className="order-details-product" style={{ opacity: isDeleted ? 0.25 : 1, pointerEvents: isDeleted ? 'none' : 'auto' }}>
                    <div className="order-details-product-main">
                        <div className="order-details-product-image">
                        {product.product_image && (
                            <img src={product.product_image} alt={product.product_name} />
                        )}
                        </div>
                        <div className="order-details-product-text">
                        <p className="order-details-product-name">{product.product_name}</p>
                        {product.custom_inputs && product.custom_inputs.length > 0 && (
                            <ul className="order-details-product-options">
                            {product.custom_inputs
                                .filter((input: any) => !['name', 'price'].includes(input.name) && input.value && typeof input.value === 'string' && input.value.trim() !== '')
                                .map((input: any, i: number) => (
                                <li key={i} style={{ textTransform: 'capitalize' }}>{input.name}: {input.value}</li>
                                ))}
                            </ul>
                        )}
                        </div>
                    </div>
                    <div className="order-details-product-pricing">
                        <span className="order-details-price">${formatPrice(product.product_price)}</span>
                        <span className="order-details-quantity">x</span>
                        <div className="order-editor-quantity-input">
                            <TextField
                            type="number"
                            value={productQuantities[index]}
                            placeholder="0"
                            onChange={(e) => handleQuantityChange(index, e.target.value)}
                            size="small"
                            disabled={isDeleted}
                            inputProps={{ 
                                min: 0,
                                style: { textAlign: 'center' }
                            }}
                            sx={{
                                backgroundColor: '#F8F8FF',
                                color: '#7963FF',
                                width: '40px',
                                borderRadius: '4px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '4px',
                                    '& fieldset': {
                                        border: 'none',
                                    },
                                    '&:hover fieldset': {
                                        border: 'none',
                                    },
                                    '&.Mui-focused fieldset': {
                                        border: 'none',
                                    },
                                },
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                                '& input[type=number]::-webkit-outer-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                                '& input[type=number]::-webkit-inner-spin-button': {
                                    WebkitAppearance: 'none',
                                    margin: 0,
                                },
                            }}
                            />
                        </div>
                        <span className="order-editor-price">
                            ${formatPrice(product.product_price * (productQuantities[index] ?? product.quantity))}
                            <span className="remove-product-button" onClick={() => !isDeleted && handleRemoveProduct(index)}>
                                Remove
                            </span>
                        </span>
                    </div>
                    </article>
                    );
                })}
                </div>
            </div>

            <div className="order-details-totals">
                <div className="order-details-total-row">
                <span className="order-details-total-spacer" aria-hidden="true" />
                <div className="order-details-total-info">
                    <span className="order-details-total-label">Subtotal</span>
                </div>
                <span className="order-details-price">${formatPrice(order.subtotal_price)}</span>
                </div>
                {Number(order.total_discount) > 0 && (
                <div className="order-details-total-row">
                    <span className="order-details-total-spacer" aria-hidden="true" />
                    <div className="order-details-total-info">
                    <span className="order-details-total-label">Discount</span>
                    </div>
                    <span className="order-details-price">-${formatPrice(order.total_discount)}</span>
                </div>
                )}
                <div className="order-details-total-row">
                <span className="order-details-total-spacer" aria-hidden="true" />
                <div className="order-details-total-info">
                    
                    <div className="order-details-total-shipping">
                    <div><span className="order-details-total-label">Shipping</span></div>
                    <div className="order-details-total-shipping-content">
                        <span className="order-details-name">{shipping.name} {shipping.lastname}</span>
                        <span className="order-details-address">{shipping.address}</span>
                        <span className="order-details-address">{shipping.city_name} {shipping.region_name} {shipping.zip_code}</span>
                        <span className="order-details-address">{shipping.country_name}</span>
                    </div>
                    <div></div>
                    </div>
                </div>
                <span className="order-details-price">${formatPrice(order.shipping_price)}</span>
                </div>
                {order.discriminated_taxes && order.discriminated_taxes.length > 0 ? (
                <div className="order-details-total-row">
                    <span className="order-details-total-spacer" aria-hidden="true" />
                    <div className="order-details-total-info">
                    <div className="order-details-total-shipping">
                        <div><span className="order-details-total-label">Taxes</span></div>
                        <div className="order-details-total-shipping-content">
                        {order.discriminated_taxes.map((tax: any, index: number) => (
                            <span key={index} className="order-details-name" style={{ marginBottom: index < order.discriminated_taxes.length - 1 ? '10px' : '0' }}>{tax.tax_type} ({formatTaxRate(tax.tax_rate)})</span>
                        ))}
                        </div>
                        <div></div>
                    </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                    {order.discriminated_taxes.map((tax: any, index: number) => (
                        <span key={index} className="order-details-price">${formatPrice(tax.tax_amount)}</span>
                    ))}
                    </div>
                </div>
                ) : (
                <div className="order-details-total-row">
                    <span className="order-details-total-spacer" aria-hidden="true" />
                    <div className="order-details-total-info">
                    <span className="order-details-total-label">Taxes</span>
                    </div>
                    <span className="order-details-price">${Number(order.total_tax).toFixed(2)}</span>
                </div>
                )}
            </div>
            
            <div className="order-details-total-row order-details-total-row-highlight">
                <span className="order-details-total-spacer" aria-hidden="true" />
                <div className="order-details-total-info">
                <span className="order-details-total-label">Total</span>
                </div>
                <span className="order-details-price">${formatPrice(order.total_price)}</span>
            </div>
            </section>

            <div className="order-details-card order-edit-reason">
                <span className="reason-label">Reason For Edit *</span>
                <TextField
                    variant="outlined"
                    fullWidth
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    sx={{ marginTop: '10px' }}
                />
                <span className="reason-tip">Only Visible to Staff</span>
            </div>
        </div>

        <aside id='order-details-summary' className="order-details-card">

          <div className="order-details-summary-section">
            <span className="order-details-summary-title">Summary</span>

            <div className="summary_item">
                <span>{hasChanges ? 'New Total' : 'Total'}</span>
                <span>${formatPrice(calculatedOrder?.newTotal ?? order.total_price)}</span>
            </div>
            <div className="summary_item">
                <span>Paid</span>
                <span>${formatPrice(order.total_price)}</span>
            </div>
            
          </div>
            
        <Divider style={{  borderColor: "#ECEBFF" }} />

        

        <div className="order-details-summary-buttons">

          {hasChanges && calculatedOrder && calculatedOrder.differenceType !== 'none' && (
              <div className="summary_item" style={{ 
                color: '#7963ff',
                fontWeight: 500,
                fontSize: '12px',
                marginBottom: '10px'
              }}>
                <span>{calculatedOrder.differenceType === 'collect' ? 'Amount to Collect' : 'Amount to Refund'}</span>
                <span>
                  {calculatedOrder.differenceType === 'collect' ? '+' : '-'}${formatPrice(Math.abs(calculatedOrder.difference))}
                </span>
              </div>
            )}

            <FormControlLabel
                control={
                    <Checkbox
                        checked={sendNotification}
                        onChange={(e) => setSendNotification(e.target.checked)}
                        sx={{
                            color: '#7963FF',
                            '&.Mui-checked': {
                                color: '#7963FF',
                            },
                        }}
                    />
                }
                label="Send a notification email to the customer"
                sx={{
                    '& .MuiFormControlLabel-label': {
                        fontSize: '9px',
                        color: '#424242',
                    }
                }}
            />

            <Button
                variant="contained"
                color="primary"
                size="small"
                fullWidth
                onClick={handleUpdateOrderClick}
                disabled={!hasChanges || !reason.trim() || updateLoading}
                sx={{
                    padding: "6px 12px",
                    borderRadius: "5px !important",
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "150%"
                }}
            >
                {updateLoading ? 'Updating...' : 'Update Order'}
            </Button>

            <Button
                variant="outlined"
                color="primary"
                size="small"
                fullWidth
                onClick={
                    () => navigate(`/orders/${order_id}`)
                }
                sx={{
                    padding: "6px 12px",
                    borderRadius: "5px !important",
                    fontSize: "14px",
                    fontWeight: "500",
                    lineHeight: "150%",
                    "&:hover": {
                        backgroundColor: "#7963FF",
                        color: "#FFFFFF",
                    }
                }}
            >
                Cancel
            </Button>

            

        </div>


        </aside>
      </div>

      {/* Confirm Order Change Dialog */}
      <ConfirmOrderChangeDialog
        open={confirmChangeDialogOpen}
        setOpen={setConfirmChangeDialogOpen}
        loading={updateLoading}
        onConfirm={handleUpdateOrder}
        differenceType={calculatedOrder?.differenceType || 'none'}
        differenceAmount={Math.abs(calculatedOrder?.difference || 0)}
        customerName={`${orderDetails?.data?.billing?.name || ''} ${orderDetails?.data?.billing?.lastname || ''}`.trim()}
        cardLogoUrl={orderDetails?.data?.order?.card_logo_url}
        currency={orderDetails?.data?.store?.currency}
        chargeFailed={chargeFailed}
        onEditPaymentInfo={handleEditPaymentInfo}
        onSendFailedNotification={handleSendFailedNotification}
        blocked={paymentBlocked}
      />

    </div>
  );
}
